import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Check } from "lucide-react";
import { toast } from "sonner";

import { StepRenderer } from "./StepRenderer";
import type { PaymentMethod } from "@/interfaces/payment-method";
import { PriceType } from "@/interfaces/advertisement";
import type { TradeType } from "@/interfaces/trade";

export type AdFormData = {
  type: TradeType;
  assetId: string;
  fiatCurrencyId: string;
  priceType: PriceType;
  price?: string;
  floatingMargin?: string;
  minAmount: string;
  maxAmount: string;
  totalAmount: string;
  paymentWindow: number;
  terms: Array<{
    title?: string;
    description: string;
    order: number;
    isActive: boolean;
  }>;
  paymentMethodIds: string[];
};

interface Props {
  initialValue?: AdFormData;
  isEditing?: boolean;
  availablePaymentMethods: PaymentMethod[] | undefined;
  onSubmit: (data: AdFormData) => void;
}

const defaultForm: AdFormData = {
  type: "" as TradeType,
  assetId: "",
  fiatCurrencyId: "",
  priceType: PriceType.FIXED,
  price: "",
  floatingMargin: "",
  minAmount: "",
  maxAmount: "",
  totalAmount: "",
  paymentWindow: 15,
  terms: [{ title: "", description: "", order: 0, isActive: true }],
  paymentMethodIds: [],
};

const steps = [
  "Asset & Type",
  "Pricing",
  "Limits & Window",
  "Payments",
  "Terms",
  "Review",
];

export default function AdFormWizard({
  initialValue,
  isEditing = false,
  availablePaymentMethods,
  onSubmit,
}: Props) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<AdFormData>(initialValue || defaultForm);

  const set = (patch: Partial<AdFormData>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const next = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const errors = useMemo(() => {
    const e: Record<string, string> = {};

    if (!form.assetId) e.assetId = "Asset is required";
    if (!form.fiatCurrencyId) e.fiatCurrencyId = "Fiat currency is required";

    if (form.priceType === PriceType.FIXED && !form.price?.trim()) {
      e.price = "Fixed price is required";
    }

    if (form.priceType === PriceType.FLOATING && !form.floatingMargin?.trim()) {
      e.floatingMargin = "Floating margin is required";
    }

    if (!form.minAmount) e.minAmount = "Min amount required";
    if (!form.maxAmount) e.maxAmount = "Max amount required";
    if (!form.totalAmount) e.totalAmount = "Total amount required";

    if (Number(form.minAmount) > Number(form.maxAmount)) {
      e.minAmount = "Min cannot exceed Max";
    }

    if (Number(form.maxAmount) > Number(form.totalAmount)) {
      e.maxAmount = "Max cannot exceed Total";
    }

    if (form.paymentWindow < 5 || form.paymentWindow > 30) {
      e.paymentWindow = "Payment window must be 5–30 minutes";
    }

    if (!form.paymentMethodIds.length) {
      e.paymentMethodIds = "At least one payment method required";
    }

    if (!form.terms.some((t) => t.description.trim())) {
      e.terms = "At least one term description required";
    }

    return e;
  }, [form]);

  const stepValidators = [
    () => !!form.assetId && !!form.fiatCurrencyId && !!form.type,
    () =>
      form.priceType === PriceType.FLOATING
        ? !!form.floatingMargin?.trim()
        : !!form.price?.trim(),
    () =>
      !!form.minAmount &&
      !!form.maxAmount &&
      !!form.totalAmount &&
      form.paymentWindow >= 5 &&
      form.paymentWindow <= 30 &&
      Number(form.minAmount) <= Number(form.maxAmount) &&
      Number(form.maxAmount) <= Number(form.totalAmount),
  ];

  const canContinue = stepValidators[step]?.() ?? true;

  // const canContinue = useMemo(() => {
  //   if (step === 0) {
  //     return !!form.assetId && !!form.fiatCurrencyId && !!form.type;
  //   }

  //   if (step === 1) {
  //     return form.priceType === PriceType.FLOATING
  //       ? !!form.floatingMargin?.trim()
  //       : !!form.price?.trim();
  //   }

  //   if (step === 2) {
  //     return (
  //       !!form.minAmount &&
  //       !!form.maxAmount &&
  //       !!form.totalAmount &&
  //       form.paymentWindow >= 5 &&
  //       form.paymentWindow <= 30 &&
  //       Number(form.minAmount) <= Number(form.maxAmount)
  //       // Number(form.maxAmount) <= Number(form.totalAmount)
  //     );
  //   }

  //   // Default for other steps
  //   return true;
  // }, [step, form]);

  const submit = () => {
    if (!canContinue) {
      toast.error("Please complete all required fields");
      return;
    }
    onSubmit(form);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* STEP INDICATOR */}
      <Card className="rounded-2xl h-fit">
        <CardContent className="p-2 space-y-4">
          <h3 className="font-semibold">Create Advertisement</h3>
          <Separator />

          {steps.map((s, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 text-sm ${
                i === step
                  ? "text-primary font-medium"
                  : "text-muted-foreground"
              }`}
            >
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center border ${
                  i < step
                    ? "bg-primary text-white border-primary"
                    : i === step
                      ? "border-primary"
                      : ""
                }`}
              >
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              {s}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* MAIN FORM */}
      <div className="lg:col-span-3 space-y-6">
        <Card className="rounded-2xl">
          <CardContent className="p-2 space-y-6">
            <StepRenderer
              errors={errors}
              step={step}
              form={form}
              set={set}
              availablePaymentMethods={availablePaymentMethods}
            />
          </CardContent>
        </Card>

        {/* NAVIGATION */}
        <div className="flex justify-between">
          <Button
            className="h-11"
            variant="outline"
            onClick={back}
            disabled={step === 0}
          >
            Back
          </Button>

          {step === steps.length - 1 ? (
            <Button className="h-11" onClick={submit}>
              {isEditing ? "Save Changes" : "Publish Advertisement"}
            </Button>
          ) : (
            <Button className="h-11" onClick={next} disabled={!canContinue}>
              Continue
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
